/**
 * Collapsible CONCEPT grouping. Concepts start collapsed and lazy-load their
 * body only once opened, matching the Google EntityGroupRow lazy pattern. The
 * body (every creative plus every asset of those creatives) arrives in two
 * batched, unfiltered requests (see useConceptCreativeTree) and renders behind a
 * single loader. Children are requested by their parent row's `_id`, and the server
 * resolves the join (CREATIVE.parentSourceEntityId === CONCEPT.sourceEntityId;
 * ASSET.parentSourceEntityId === CREATIVE.sourceEntityId), which the client reuses
 * to hand each creative its assets.
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { AdspCreativeBlock } from './AdspCreativeBlock';
import { AdspPaginationFooter } from './AdspPaginationFooter';
import { EntityDetailsToggle } from './EntityDetailsToggle';
import { EntityIcon } from './EntityIcon';
import { readMetadataName } from './adspEntityHelpers';
import { EMPTY_VALUE, StatusBadge } from './StatusBadge';
import { type ConceptCreativeTree, type TreeLevelPaging, useConceptCreativeTree } from './useConceptCreativeTree';

interface AdspConceptBlockProps {
  campaignId: string;
  concept: PublisherEntityDto;
}

const NO_ASSETS: PublisherEntityDto[] = [];

export const AdspConceptBlock: React.FC<AdspConceptBlockProps> = ({ campaignId, concept }) => {
  const [hasExpanded, setHasExpanded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const name = readMetadataName(concept) ?? concept.sourceEntityId ?? EMPTY_VALUE;

  const tree = useConceptCreativeTree({
    campaignId,
    conceptId: concept._id,
    skip: !hasExpanded,
  });

  const handleToggle = (): void => {
    if (!hasExpanded) setHasExpanded(true);
    setIsExpanded(prev => !prev);
  };

  return (
    <Box data-testid={`ads-manager-concept-${concept._id}`} spacingInset="300" type="outline">
      <Column spacing="300">
        <ConceptHeader
          concept={concept}
          creativeTotal={tree.creativePaging.pagination?.total}
          isExpanded={isExpanded}
          name={name}
          onToggle={handleToggle}
        />
        {concept.lastError != null && (
          <Text color="error" type="b200">{concept.lastError}</Text>
        )}

        {isExpanded && (
          <Column spacing="300">
            <EntityDetailsToggle entity={concept} testId={`ads-manager-concept-${concept._id}`} />
            <ConceptBody conceptId={concept._id} tree={tree} />
          </Column>
        )}
      </Column>
    </Box>
  );
};

interface ConceptHeaderProps {
  concept: PublisherEntityDto;
  creativeTotal?: number;
  isExpanded: boolean;
  name: string;
  onToggle: () => void;
}

const ConceptHeader: React.FC<ConceptHeaderProps> = ({ concept, creativeTotal, isExpanded, name, onToggle }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{ alignItems: 'center', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
      onClick={onToggle}
    >
      <Row alignmentVertical="center" spacing="200">
        <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
        <EntityIcon size={28} type={concept.publisherEntityType} />
        <Text type="b300">{name}</Text>
        {creativeTotal != null && creativeTotal > 0 && (
          <Text color="secondary" type="b100">
            {t('ads_manager_creatives_label')} ({creativeTotal})
          </Text>
        )}
      </Row>
      <StatusBadge entity={concept} />
    </div>
  );
};

interface ConceptBodyProps {
  conceptId: string;
  tree: ConceptCreativeTree;
}

const ConceptBody: React.FC<ConceptBodyProps> = ({ conceptId, tree }) => {
  const { t } = useTranslation();
  const { assetPaging, assetsByCreative, creativePaging, creatives } = tree;

  if (tree.isError) {
    return (
      <Alert data-testid={`ads-manager-concept-creatives-error-${conceptId}`} size="small" type="error">
        {t('ads_manager_query_error')}
      </Alert>
    );
  }
  if (tree.isLoading) {
    return (
      <div data-testid={`ads-manager-concept-loading-${conceptId}`}>
        <MeridianLoader size="small" type="linear" />
      </div>
    );
  }
  if (creatives.length === 0) {
    return (
      <Text color="secondary" data-testid={`ads-manager-concept-no-creatives-${conceptId}`} type="b100">
        {t('ads_manager_no_concept_creatives')}
      </Text>
    );
  }

  return (
    <Column spacing="300">
      <Column data-testid={`ads-manager-concept-creatives-${conceptId}`} spacing="200">
        {creatives.map(creative => (
          <AdspCreativeBlock
            assets={assetsByCreative.get(creative.sourceEntityId ?? '') ?? NO_ASSETS}
            creative={creative}
            key={creative._id}
          />
        ))}
      </Column>
      <TreeLevelFooter paging={creativePaging} testId={`ads-manager-concept-creatives-${conceptId}`} />
      {/* Assets arrive in one batch per creative page; page through only when that batch overflows. */}
      {(assetPaging.pagination?.pageCount ?? 0) > 1 && (
        <TreeLevelFooter paging={assetPaging} testId={`ads-manager-concept-assets-${conceptId}`} />
      )}
    </Column>
  );
};

const TreeLevelFooter: React.FC<{ paging: TreeLevelPaging; testId: string }> = ({ paging, testId }) => {
  if (paging.pagination == null) return null;
  return (
    <AdspPaginationFooter
      isFetching={paging.isFetching}
      page={paging.page}
      pagination={paging.pagination}
      testId={testId}
      onPageChange={paging.setPage}
    />
  );
};
